import { useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    useDeleteUserAgentApi,
    UserAgentDTO,
    useSaveUserAgentApi,
    useSearchUserAgentApi,
} from '../../../api/user-agent-api';
import SvgAdd from '../../../assets/icons/Add';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { LoadingButton } from '../../../components/loading-button';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveUserAgentModal } from './save-user-agent-modal';

export function UserAgentApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedUserAgent, setSelectedUserAgent] = useState<UserAgentDTO | undefined>();
    const userAgentInit = useSearchUserAgentApi();
    const saveUserAgent = useSaveUserAgentApi();
    const deleteUserAgent = useDeleteUserAgentApi();

    const { result, error, busy, initialized, initializing } = userAgentInit;

    const columns: IBasicTableColumn<UserAgentDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 80,
                Cell: ({ row }) => <N>{row.id}</N>,
            },
            {
                Header: <T>value</T>,
                name: 'value',
                width: 80,
            },
            {
                Header: <T>os</T>,
                name: 'os',
                width: 80,
            },
            {
                Header: <T>browser</T>,
                name: 'browser',
                width: 80,
            },
            {
                Header: <T>device</T>,
                name: 'device',
                width: 80,
            },
            {
                Header: <T>brand</T>,
                name: 'brand',
                width: 80,
            },
            {
                Header: <T>model</T>,
                name: 'model',
                width: 80,
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        userAgentInit.post(null, {});
    }, [initialized, initializing, error, userAgentInit]);

    const handleDelete = (userAgent: UserAgentDTO) => {
        bd.msgbox(
            translate('delete-user-agent'),
            translate('confirm-delete-user-agent'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteUserAgent.remove(
                            userAgent,
                            () => {
                                userAgentInit.updateResult({
                                    ...result,
                                    userAgents: result.userAgents.filter(
                                        b => b.id !== userAgent.id
                                    ),
                                });
                                notify.dark(translate('selected-user-agent-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedUserAgent(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (userAgent: UserAgentDTO) => {
        saveUserAgent.post(
            null,
            userAgent,
            res => {
                if (selectedUserAgent) {
                    userAgentInit.updateResult({
                        ...result,
                        userAgents: result.userAgents.map(b =>
                            b.id === userAgent.id ? res.userAgent : b
                        ),
                    });
                } else {
                    userAgentInit.updateResult({
                        ...result,
                        userAgents: [res.userAgent, ...result.userAgents],
                    });
                }

                setShowModal(false);
                setSelectedUserAgent(undefined);
                notify.success(
                    selectedUserAgent
                        ? translate('selected-user-agent-updated-successfully')
                        : translate('new-user-agent-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => userAgentInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar onGoBack="/home" title="user-agents" />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.userAgents}
                        onRowClick={({ row }) => {
                            setSelectedUserAgent(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveUserAgentModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedUserAgent(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedUserAgent={selectedUserAgent}
                        allowEdit={false}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
